import { Button, IconButton, styled, useTheme, Modal } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-gray.svg';
import { Typography } from '../../../../components/Typography/Typography';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { overideDataAtom } from '../../../../state/UITimesheetsState';
import { useOverideHolidayTableColumn } from '../../hooks/useOverideHolidayTableColumn';
import { TableV1 } from '../../../../components/Table/TableV1';
import { useOverideTimesheet } from '../../../../queries/hooks/useOverideTimesheet';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { LoadingId } from '../../../../types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const Wrapper = styled('div')`
  width: 557px;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg1};
  border-radius: 4px;
  position: absolute;
  top: 100px;
  left: 50%;
  transform: translate(-50%, 0);
  display: flex;
  flex-direction: column;
`;

const Content = styled('div')`
  padding: 24px;
`;
const TitleWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const ButtonsWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px 24px 24px 24px;
`;

export const OverideHolidayModal: FC<Props> = ({ open, onClose }) => {
  const { colors } = useTheme();
  const [allowOveride, setAllowOveride] = useState(false);
  const overideHolidayData = useAtomValue(overideDataAtom);
  const { mutate: onOverideTimesheet, isLoading: overideTimesheetInProgress } =
    useOverideTimesheet();
  const { pushErrorToast, pushSuccessToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const note = allowOveride
    ? 'Select the Deal you were working on during this Company Event and enter the time to log.'
    : 'This day includes a scheduled Company Event. Are you sure you want to override it and log time to a specific Deal instead?';

  const title = allowOveride ? 'Log time to Deal' : 'Override Company Event';

  const confirmLabel = allowOveride ? 'Save' : 'Yes, Override';

  const columns = useOverideHolidayTableColumn(
    overideHolidayData?.header || '',
    overideHolidayData?.holidayName || ''
  );

  const total = useMemo(() => {
    if (!overideHolidayData) return 0;
    return overideHolidayData.data.reduce((acc, item) => acc + (item.data || 0), 0);
  }, [overideHolidayData]);

  const onConfirm = useCallback(() => {
    if (allowOveride) {
      startLoading(LoadingId.overideTimesheet);
      onOverideTimesheet(
        {
          payload: {
            dayName: overideHolidayData?.weekday || '',
            day: overideHolidayData?.day || 0,
            data: overideHolidayData?.data || [],
          },
          id: overideHolidayData?.tsId || 0,
        },
        {
          onSuccess: () => {
            pushSuccessToast({
              title: 'Timesheet Updated',
              message: `Timesheet has been successfully overridden`,
            });
          },
          onError: (error: unknown) => {
            pushErrorToast({
              message: extractErrorMessage(error),
            });
          },
          onSettled: () => {
            setAllowOveride(false);
            onClose();
            stopLoading(LoadingId.overideTimesheet);
          },
        }
      );
    } else {
      setAllowOveride(true);
    }
  }, [
    allowOveride,
    onClose,
    onOverideTimesheet,
    overideHolidayData?.data,
    overideHolidayData?.day,
    overideHolidayData?.tsId,
    overideHolidayData?.weekday,
    pushErrorToast,
    pushSuccessToast,
    startLoading,
    stopLoading,
  ]);

  const onCancel = useCallback(() => {
    setAllowOveride(false);
    onClose();
  }, [onClose]);

  const table = useReactTable({
    data: overideHolidayData?.data ?? [],
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (item) => String(item.id),
  });

  return (
    <Modal open={open} disableAutoFocus={true} disableEnforceFocus={true}>
      <Wrapper>
        <Content>
          <TitleWrapper>
            <Typography variant='h5' color={colors.text.main}>
              {title}
            </Typography>
            <IconButton onClick={onClose} style={{ marginRight: '-10px' }}>
              <CloseIcon />
            </IconButton>
          </TitleWrapper>
          <Typography variant='body' color={colors.text.main}>
            {note}
          </Typography>
          {allowOveride && (
            <TableV1
              className='single-timesheet'
              hasFooter
              table={table}
              height='auto'
              isLastColumnSticky={false}
              disableRowHover
              minWidth='unset'
              wrapperStyle={{
                maxHeight: 'calc(100vh - 288px)',
                overflowX: 'scroll',
                border: `1px solid ${colors.border.default}`,
                overflowY: 'scroll',
                marginTop: '16px',
              }}
            />
          )}
        </Content>
        <ButtonsWrapper>
          <Button variant='outlined' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={onConfirm}
            disabled={(allowOveride && total !== 100) || overideTimesheetInProgress}
          >
            {confirmLabel}
          </Button>
        </ButtonsWrapper>
      </Wrapper>
    </Modal>
  );
};
